import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

const DEFAULT_BRANDING = {
  primary_color: '#DC2626',
  sidebar_color: '#0F1730',
  accent_color: '#B91C1C',
  brand_name: null,
};

const STORAGE_KEY = 'dotbox_branding';

const useBranding = () => {
  const [branding, setBranding] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_BRANDING;
    } catch (e) {
      return DEFAULT_BRANDING;
    }
  });

  const brandingRef = useRef(branding);
  useEffect(() => { brandingRef.current = branding; }, [branding]);

  // Apply CSS variables whenever branding changes
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', branding.primary_color);
    root.style.setProperty('--color-sidebar', branding.sidebar_color);
    root.style.setProperty('--color-accent', branding.accent_color);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
  }, [branding]);

  // Fetch from API on mount
  useEffect(() => {
    api.get('/users/branding')
      .then(r => {
        if (r.data) setBranding(prev => ({ ...prev, ...r.data }));
      })
      .catch((err) => { console.error('Branding API fetch failed:', err); });
  }, []);

  const updateBranding = useCallback(async (updates) => {
    const newBranding = { ...brandingRef.current, ...updates };
    setBranding(newBranding);
    try {
      await api.patch('/users/branding', newBranding);
    } catch (e) {
      console.error('Branding save failed:', e);
    }
  }, []);

  const resetBranding = useCallback(async () => {
    setBranding(DEFAULT_BRANDING);
    try {
      await api.patch('/users/branding', DEFAULT_BRANDING);
    } catch (e) {
      console.error('Branding reset failed:', e);
    }
  }, []);

  return { branding, updateBranding, resetBranding, DEFAULT_BRANDING };
};

export default useBranding;
